
import React, { useRef } from 'react';
import { Eye, EyeOff, GripHorizontal } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export interface DraggableKpiCardProps {
  id: string;
  index: number;
  title: string;
  value: string;
  unit: string;
  trend?: string;
  isPositive?: boolean | null;
  icon?: React.ReactNode;
  colorClass?: string;
  isDraggable?: boolean;
  onHide?: () => void;
  onClick?: () => void;
}

export const DraggableKpiCard: React.FC<DraggableKpiCardProps> = ({
  id,
  index,
  title,
  value,
  unit,
  trend,
  isPositive,
  icon,
  colorClass = "bg-blue-50 dark:bg-blue-900/20",
  isDraggable = false,
  onHide,
  onClick
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleCardClick = (e: React.MouseEvent) => {
    // If we're not in edit mode and a click handler is provided, call it
    if (!isDraggable && onClick) {
      onClick();
    }
  };

  return (
    <Card
      ref={cardRef}
      className={cn(
        "relative overflow-hidden transition-all duration-200 theme-transition kpi-card",
        isDraggable ? "cursor-grab" : "cursor-pointer hover:shadow-md hover:scale-[1.02]",
      )}
      onClick={handleCardClick}
    >
      <CardContent className="p-4 flex items-start">
        <div className="w-full space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {icon && (
                <div className={cn("p-2 rounded-full mr-3", colorClass)}>
                  {icon}
                </div>
              )}
              <h3 className="font-medium text-foreground">{title}</h3>
            </div>

            {isDraggable && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 ml-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  onHide && onHide();
                }}
              >
                <EyeOff className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="ml-2">
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-foreground">{value}</span>
              {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
            </div>
            
            {trend && (
              <p 
                className={cn("text-xs mt-1", 
                  isPositive === true ? "text-green-600 dark:text-green-400" : 
                  isPositive === false ? "text-red-600 dark:text-red-400" : 
                  "text-muted-foreground"
                )}
              >
                {trend}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DraggableKpiCard;
